import { Wrapper as GoogleMapsWrapper } from "@googlemaps/react-wrapper"
import {
  Address,
  Address_Country as Country,
} from "gen/proto/address/models_pb"
import { ChangeEventHandler, FocusEventHandler, useEffect } from "react"
import { CA_PROVINCES, US_STATES } from "shared-ts/libs/country"

import {
  useAddressAutocomplete,
  type AutocompleteAddress,
} from "../../../../hooks/useAddressAutocomplete"
import { Combobox } from "../../../base/input/Combobox"
import { TextInput } from "../../../base/input/text/TextInput"
import { CountrySelector } from "../CountrySelector"

export interface AddressInputsProps {
  address: Address
  onChangeAddress: (address: Address) => void
  // Fallback country when `address.country` is unset (e.g. the company country).
  defaultCountry: Country
  isOptional?: boolean
  errorMessage?: string
  isInModal?: boolean
  label?: string
  // When true, the country field is editable. Forms that must stay locked to the
  // company country (locations, staff, PO custom address) omit this prop.
  enableCrossCountry?: boolean
  autoFocus?: boolean
  onBlur?: FocusEventHandler<HTMLDivElement>
}

type USState = (typeof US_STATES)[number]
type CAProvince = (typeof CA_PROVINCES)[number]
type StateOrProvince = USState | CAProvince

export function AddressInputs({
  address,
  onChangeAddress,
  defaultCountry,
  isOptional,
  errorMessage,
  isInModal,
  label = "Address",
  enableCrossCountry,
  autoFocus,
  onBlur,
}: AddressInputsProps) {
  const country = address.country || defaultCountry
  const { addresses, setValue, resolveAddress, googleMapsWrapperCallback } =
    useAddressAutocomplete(country)

  useEffect(() => {
    setValue(address.line1)
  }, [address.line1])

  const onSelectAddress = async (suggestion: AutocompleteAddress | null) => {
    if (!suggestion?.placeId) {
      return
    }

    const resolved = await resolveAddress(suggestion.placeId)
    onChangeAddress({ ...resolved, line2: address.line2 })
  }

  function onChangeInput(
    key: keyof Address
  ): ChangeEventHandler<HTMLInputElement> {
    return (e) => {
      onChangeAddress({
        ...address,
        [key]: e.target.value,
      })
    }
  }

  return (
    <GoogleMapsWrapper
      apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!}
      libraries={["places"]}
      callback={googleMapsWrapperCallback}
    >
      <div className="contents" onBlur={onBlur}>
        <div className="sm:col-span-4">
          <Combobox<AutocompleteAddress>
            label={label}
            secondaryLabel={isOptional ? "optional" : undefined}
            items={addresses}
            selectedItem={address}
            onSelectItem={onSelectAddress}
            keyExtractor={(item) =>
              item.placeId ?? `${item.line1}-${item.city}-${item.state}`
            }
            valueExtractor={(item) => item.description ?? item.line1}
            errorMessage={errorMessage}
            onChangeQuery={(q) =>
              onChangeAddress({
                ...address,
                line1: q,
              })
            }
            eq={(a, b) =>
              a?.line1 === b?.line1 &&
              a?.city === b?.city &&
              a?.state === b?.state
            }
            placeholder=""
            optionsClassName={isInModal ? "z-50" : undefined}
            autoFocus={autoFocus}
            hideChevronButton
          />
        </div>
        <div className="sm:col-span-2">
          <TextInput
            data-testid="address-line-2-input"
            id="address-line-2"
            label="Apt, suite, etc."
            value={address.line2}
            onChange={onChangeInput("line2")}
          />
        </div>
        <div className="sm:col-span-2">
          <TextInput
            data-testid="address-city-input"
            id="city"
            label="City"
            value={address.city}
            onChange={onChangeInput("city")}
            invalid={!!errorMessage}
          />
        </div>
        <div className="shadow-none sm:col-span-1">
          <Combobox<StateOrProvince>
            data-testid="address-state-input"
            label={country === Country.CA ? "Province" : "State"}
            placeholder=""
            items={[...(country === Country.CA ? CA_PROVINCES : US_STATES)]}
            selectedItem={address.state as StateOrProvince}
            onSelectItem={(state: StateOrProvince | null) =>
              onChangeAddress({ ...address, state: state ?? "" })
            }
            keyExtractor={(s) => s}
            valueExtractor={(s) => s}
            invalid={!!errorMessage}
            optionsClassName={isInModal ? "z-50" : undefined}
            isOpenOnFocus
          />
        </div>
        <div className="sm:col-span-1">
          <TextInput
            data-testid="address-zip-input"
            id="zip"
            label={country === Country.CA ? "Postal code" : "ZIP"}
            value={address.postalCode}
            onChange={onChangeInput("postalCode")}
            invalid={!!errorMessage}
          />
        </div>
        <CountrySelector
          value={country}
          onChange={
            enableCrossCountry
              ? (c) => onChangeAddress({ ...address, country: c })
              : undefined
          }
          isInModal={isInModal}
        />
      </div>
    </GoogleMapsWrapper>
  )
}
